/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from "react";
import { Menu, X, Scale, Globe } from "lucide-react";
import logo from "../assets/new-logo.png";
import { useLang } from "../i18n/LanguageContext";

function Navbar() {
  const { t, toggle } = useLang();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { label: t.nav.home, href: "#banner-0" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.clients, href: "#clients" },
    { label: t.nav.team, href: "#team" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        isSticky
          ? "border-primary/10 bg-paper/85 shadow-[0_8px_30px_rgba(5,37,45,0.08)] backdrop-blur-md"
          : "border-transparent bg-paper/40 backdrop-blur-sm"
      }`}
    >
      <nav className="container mx-auto px-6 py-3 lg:px-24">
        <div className="flex items-center justify-between">
          {/* Breathing monogram */}
          <a href="#banner-0" className="flex items-center gap-3">
            <span className="monogram-breath grid h-12 w-12 place-items-center rounded-full border border-secondary/50 bg-white/70">
              <img src={logo} width={32} alt="Al-Rashad" />
            </span>
            <span className="hidden flex-col leading-none sm:flex">
              <span className="font-serif text-lg font-semibold tracking-wide text-primary">
                {t.nav.brand}
              </span>
              <span className="text-[10px] uppercase tracking-eyebrow text-gold-deep">
                {t.nav.brandSub}
              </span>
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden items-center gap-7 md:flex">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="group relative text-sm tracking-wide text-primary/70 transition-colors hover:text-primary"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 right-auto h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full rtl:left-auto rtl:right-0" />
              </a>
            ))}

            <LangToggle label={t.nav.switchTo} onClick={toggle} />

            <a
              href="#contact"
              className="rounded-full border border-secondary/70 px-4 py-2 text-sm text-primary transition-colors hover:bg-secondary hover:text-white"
            >
              {t.nav.cta}
            </a>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <LangToggle label={t.nav.switchTo} onClick={toggle} />
            <button
              onClick={() => setIsMobileMenuOpen((o) => !o)}
              className="text-primary transition-colors hover:text-gold-deep"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile dropdown */}
      <div
        className={`overflow-hidden border-t border-primary/10 bg-paper/95 backdrop-blur-md transition-[max-height] duration-300 md:hidden ${
          isMobileMenuOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="flex flex-col px-6 py-4">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 border-b border-primary/10 py-3 text-primary/80 last:border-0 hover:text-gold-deep"
            >
              <Scale className="h-4 w-4 text-secondary" />
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="mt-4 rounded-full border border-secondary/70 py-3 text-center text-primary hover:bg-secondary hover:text-white"
          >
            {t.nav.cta}
          </a>
        </div>
      </div>
    </header>
  );
}

/** Globe + native-language-name button that swaps EN <-> AR. */
function LangToggle({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Switch language to ${label}`}
      className="flex items-center gap-1.5 rounded-full border border-secondary/50 px-3 py-1.5 text-sm text-primary transition-colors hover:bg-secondary hover:text-white"
    >
      <Globe className="h-4 w-4" />
      <span className="font-medium">{label}</span>
    </button>
  );
}

export default Navbar;
